"use client";

import { useQuery } from "@tanstack/react-query";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import {
  getAuthorizedComediansAndVenuesFrontEnd,
  useSession,
} from "@punchup/data/legacy";

/**
 * useIsAuthorizedToSeeDashboards()
 * Evaluates whether a user is allowed to see any comedian or venue dashboard.
 *
 * Backed by React Query keyed on the user id, so every mount across the app
 * (e.g. Navbar, dashboard layouts, UserRolesProvider) shares a single
 * `/api/user-status` request instead of each firing its own fetch.
 *
 * @returns
 * - comediansAuthorized / venuesAuthorized: managed ids (empty until loaded)
 * - isLoading: true until the first result resolves
 * - userIsAuthorizedToSeeAnyDashboards: boolean
 */
export default function useIsAuthorizedToSeeDashboards() {
  const supabase = createClientComponentClient<Database>();
  const session = useSession();
  const userId = session?.user?.id ?? null;

  const { data, isError } = useQuery({
    queryKey: ["authorized-dashboards", userId],
    queryFn: () => getAuthorizedComediansAndVenuesFrontEnd(supabase),
    // Wait until the session has resolved (undefined = still loading).
    enabled: session !== undefined,
    // Single attempt, settle immediately on failure. The fetcher normally
    // resolves to empty arrays, but a raw fetch() network error rejects the
    // queryFn; without this, React Query's default retries keep isLoading true
    // for ~7s instead of settling like the original try/finally did.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const comediansAuthorized = data?.comedians ?? [];
  const venuesAuthorized = data?.venues ?? [];
  // Loading until we have a first answer; on error we settle (isError true).
  const isLoading = data === undefined && !isError;

  return {
    comediansAuthorized,
    venuesAuthorized,
    isLoading,
    userIsAuthorizedToSeeAnyDashboards:
      comediansAuthorized.length > 0 || venuesAuthorized.length > 0,
  };
}
